<template>
  <div class="chart-report">
    <div class="chart-report__top">
      <button type="button" class="btn btn-mantraz" :disabled="isLoading" @click="openCreate">Create Chart</button>
      <button type="button" class="btn btn-outline-secondary" @click="$refs.guide.open()">Guide</button>
    </div>

    <div v-if="isLoading" class="text-center py-5"><wave-loader /></div>
    <div v-else-if="!charts.length" class="text-muted text-center py-5">No charts yet. Click Create Chart to build one.</div>
    <draggable
      v-else
      v-model="charts"
      handle=".chart-card__header"
      filter=".chart-card__actions"
      :prevent-on-filter="false"
      ghost-class="chart-report__ghost"
      class="chart-report__grid"
      @end="persistOrder"
    >
      <div
        v-for="chart in charts"
        :key="chart.id"
        :class="chart.width === 'half' ? 'chart-report__cell chart-report__cell--half' : 'chart-report__cell'"
      >
        <chart-card
          :chart="chart"
          :assets="assets"
          :device-name-by-uid="deviceNameByUid"
          @edit="openEdit"
          @delete="remove"
          @toggle-width="toggleWidth"
        />
      </div>
    </draggable>

    <chart-guide-modal ref="guide" />
    <chart-builder-modal ref="builder" :assets="assets" :device-name-by-uid="deviceNameByUid" @save="onSave" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import ChartCard from '@/components/customChart/ChartCard.vue';
import ChartBuilderModal from '@/components/customChart/ChartBuilderModal.vue';
import ChartGuideModal from '@/components/customChart/ChartGuideModal.vue';
import {
  BLOCKED, openChartDb, loadCharts, saveChart, deleteChart, saveOrder,
} from '@/util/chart/customChartStorage';

const GUIDE_SEEN_KEY = 'customChartGuideSeen';

export default {
  name: 'ChartReport',
  components: {
    draggable, ChartCard, ChartBuilderModal, ChartGuideModal,
  },
  data() {
    return {
      isLoading: true,
      charts: [],
      db: null,
      assetsFailed: false,
    };
  },
  computed: {
    assets() {
      return this.$store.state.asset.assets || [];
    },
    deviceNameByUid() {
      // Aset gagal dimuat: null = semua device dianggap dikenal, gak semua seri ditandai "vessel not found".
      if (this.assetsFailed) return null;
      const out = {};
      this.assets.forEach((a) => (a.devices || []).forEach((d) => { out[d.devcUniqueId] = d.devcName; }));
      return out;
    },
    companyId() {
      const active = this.$store.getters['auth/activeCompany'];
      if (active && active.compId) return active.compId;
      try {
        const stored = JSON.parse(localStorage.getItem('activeCompany') || 'null');
        return stored && stored.compId ? stored.compId : null;
      } catch (e) {
        return null;
      }
    },
  },
  async mounted() {
    try {
      if (!this.assets.length) await this.$store.dispatch('asset/getAssetsData');
      // getAssetsData selalu ngisi `devices` array (bisa kosong), jadi cek isinya.
      if (this.assets.some((a) => !a.devices || !a.devices.length)) await this.$store.dispatch('asset/getAssetsDevices');
    } catch (e) {
      this.assetsFailed = true;
      console.warn('[custom chart] muat aset gagal', e && e.message);
      this.$toast.error('Failed to load vessels. Series may show no data.');
    }
    try {
      this.db = await openChartDb();
      this.charts = await loadCharts(this.db, this.companyId);
    } catch (e) {
      this.db = null;
      console.warn('[custom chart] penyimpanan gagal', e && e.message);
      this.$toast.error(e && e.message === BLOCKED
        ? 'Close other Mantraz tabs, then reload this page.'
        : 'Saved charts are unavailable in this browser.');
    } finally {
      this.isLoading = false;
    }
    // Panduan muncul sendiri sekali per browser.
    try {
      if (!localStorage.getItem(GUIDE_SEEN_KEY)) {
        localStorage.setItem(GUIDE_SEEN_KEY, '1');
        this.$nextTick(() => this.$refs.guide.open());
      }
    } catch (e) {
      // Storage diblok: jangan paksa buka tiap kunjungan.
    }
  },
  beforeDestroy() {
    if (this.db) this.db.close();
  },
  methods: {
    openCreate() {
      const order = this.charts.reduce((max, c) => Math.max(max, Number(c.order) || 0), -1) + 1;
      this.$refs.builder.open(null, { companyId: this.companyId, order });
    },
    openEdit(chart) {
      this.$refs.builder.open(chart, { companyId: this.companyId, order: chart.order });
    },
    async persist(chart) {
      if (this.db) await saveChart(this.db, chart);
      const i = this.charts.findIndex((c) => c.id === chart.id);
      if (i === -1) this.charts.push(chart);
      else this.$set(this.charts, i, chart);
    },
    async onSave(chart) {
      try {
        await this.persist(chart);
        this.$toast.success(this.db ? 'Chart saved' : 'Chart added for this session only');
      } catch (e) {
        console.warn('[custom chart] simpan gagal', e && e.message);
        this.$toast.error('Failed to save chart');
      }
    },
    async toggleWidth(chart) {
      try {
        await this.persist({ ...chart, width: chart.width === 'half' ? 'full' : 'half' });
      } catch (e) {
        console.warn('[custom chart] simpan lebar gagal', e && e.message);
        this.$toast.error('Failed to save chart width');
      }
    },
    async remove(chart) {
      const ok = await this.$bvModal.msgBoxConfirm(`Delete "${chart.name}"?`, {
        title: 'Delete chart', okVariant: 'danger', okTitle: 'Delete', cancelTitle: 'Cancel', centered: true,
      });
      if (!ok) return;
      try {
        if (this.db) await deleteChart(this.db, chart.id);
        this.charts = this.charts.filter((c) => c.id !== chart.id);
        await this.persistOrder();
      } catch (e) {
        console.warn('[custom chart] hapus gagal', e && e.message);
        this.$toast.error('Failed to delete chart');
      }
    },
    async persistOrder() {
      this.charts = this.charts.map((c, i) => ({ ...c, order: i }));
      if (!this.db) return;
      try {
        await saveOrder(this.db, this.charts);
      } catch (e) {
        console.warn('[custom chart] simpan urutan gagal', e && e.message);
      }
    },
  },
};
</script>

<style scoped>
.chart-report { padding: 12px 16px; }
.chart-report__top { margin-bottom: 12px; display: flex; gap: 8px; }
.chart-report__grid { display: flex; flex-wrap: wrap; gap: 16px; }
.chart-report__cell { flex: 1 1 100%; min-width: 0; }
.chart-report__cell--half { flex: 1 1 calc(50% - 8px); max-width: calc(50% - 8px); }
.chart-report__ghost { opacity: 0.5; }

@media (max-width: 991px) {
  .chart-report__cell--half { flex-basis: 100%; max-width: 100%; }
}
</style>
